<template>
  <header id="header">

    <nav class="navbar navbar-expand-lg navbar stihl-navbar">

      <a class="navbar-brand" :href="baseURL">
        <img id="logo" src="@/assets/img/stihl-logo.svg" alt="STIHL">
        <span id="app-name" @click.prevent="$router.push({ path: '/' })">Business Access Management</span>
      </a>

      <button
        class="navbar-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#navbarContent"
        aria-controls="navbarContent"
        aria-expanded="false"
        aria-label="Toggle navigation"
        v-if="isUserLoggedIn">
        <i class="navbar-toggler-icon" />
      </button>

      <div id="navbarContent" class="collapse navbar-collapse justify-content-end" v-if="isUserLoggedIn">
        <ul class="navbar-nav ml-auto">

          <li id="tutorials" class="nav-item">
            <a class="nav-icon-link" @click="showTutorialsModal = true" @keyup.space="showTutorialsModal = true">
              <img src="@/assets/img/icons/play.svg" alt="$t('adm.button.tutorials')">
              <span class="nav-text">{{ $t('adm.button.tutorials') }}</span>
            </a>
          </li>

          <li id="contact" class="nav-item">
            <a :href="$t('adm.link.contact')" class="nav-icon-link" target="_blank" rel="noopener noreferrer">
              <img src="@/assets/img/icons/envelope.svg" alt="$t('adm.button.contact')">
              <span class="nav-text">{{ $t('adm.button.contact') }}</span>
            </a>
          </li>

          <li id="faq" class="nav-item">
            <a :href="$t('adm.link.faq')" class="nav-icon-link" target="_blank" rel="noopener noreferrer">
              <img src="@/assets/img/icons/questionmark.svg" alt="$t('adm.button.faq')">
              <span class="nav-text">{{ $t('adm.button.faq') }}</span>
            </a>
          </li>

          <li id="lang" class="nav-item dropdown">
            <a
              class="nav-icon-link"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              aria-label="Language">
              <img src="@/assets/img/icons/globe.svg" alt="Language">
              <span class="nav-text dropdown-toggle">{{ currentLocale }}</span>
            </a>
            <div class="languages dropdown-menu dropdown-menu-end">
              <ul class="row">
                <li v-for="lang in languages" :key="lang.locale" class="col-4">
                  <a
                    href="#"
                    class="dropdown-item"
                    :class="{ active: i18n.global.locale === lang.locale }"
                    @click="setLocale(lang.locale)">{{ lang.name }}</a>
                </li>
              </ul>
            </div>
          </li>

          <li id="user" class="nav-item dropdown">
            <a
              class="nav-icon-link"
              role="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              aria-label="User"
              :title="adminRole">
              <img src="@/assets/img/icons/user-notification.svg" alt="{{ displayName }}">
              <span class="nav-text dropdown-toggle">{{ displayName }}</span>
            </a>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a id="profile-link" class="dropdown-item" href="/user/ui" target="_blank">
                {{ $t('adm.button.profile') }}
              </a></li>
              <li v-if="adminRole === 'orgSuperAdmin' || adminRole === 'superAdmin'">
                <button
                  id="settings-link"
                  type="button"
                  class="dropdown-item"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#ocSettings">
                  <i class="bi bi-gear" /> {{ $t('adm.button.settings') }}
                </button>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li v-if="showAppsLink">
                <router-link id="apps-link" class="dropdown-item" :to="{ name: 'apps' }">
                  {{ $t('adm.label.application', 2) }}
                </router-link>
              </li>
              <li v-if="adminRole === 'superAdmin'">
                <router-link id="licenses-link" class="dropdown-item" :to="{ name: 'licenses' }">
                  {{ $t('adm.page.licenseMgr.title') }}</router-link>
              </li>
              <li v-if="adminRole === 'superAdmin'">
                <router-link id="roles-link" class="dropdown-item" :to="{ name: 'roles' }">
                  {{ $t('adm.page.roleMgr.title') }}</router-link>
              </li>
              <li v-if="adminRole === 'superAdmin' || adminRole === 'orgSuperAdmin'">
                <router-link id="sync-link" class="dropdown-item" :to="{ name: 'sync-org' }">
                  {{ $t('adm.page.orgCheck.title') }}</router-link>
              </li>
              <li>
                <hr class="dropdown-divider">
              </li>
              <li><a id="logout-link" class="dropdown-item" @click="logout" @keyup.enter="logout">
                {{ $t('adm.button.logout') }}
              </a></li>
            </ul>
          </li>

        </ul>
      </div>

    </nav>

    <user-preferences id="ocSettings" />

  </header>
</template>

<script lang="ts">
import userManager from '@/components/oidc';
import { defineComponent } from 'vue';
import { mapState, mapWritableState } from 'pinia';
import { i18n, languageOptions } from '@/lang';
import { forwardToLocale } from '@/routes';
import { AdminRole } from '@/components/backend';
import useAppStore from '@/components/store';
import UserPreferences from './UserPreferences.vue';

export default defineComponent({
  name: 'StihlHeader',
  components: { UserPreferences },
  methods: {

    logout() {
      userManager.signoutRedirect();
      return false;
    },
    roleDisplayName(role: AdminRole) {
      return this.$t(`admin.label.adminRoles.${role}`);
    },
    setLocale(locale: string) {
      forwardToLocale(locale, this.$route);
    },
  },
  computed: {
    ...mapState(useAppStore, {
      profile: 'principal',
      adminRole: 'getMyAdminRole',
      displayName: 'myDisplayName',
    }),
    ...mapWritableState(useAppStore, {
      showTutorialsModal: 'showTutorialsModal',
    }),
    i18n: () => (i18n),
    isUserLoggedIn(): boolean { return !!this.profile; },
    role() { return !this.adminRole ? '' : this.roleDisplayName(this.adminRole); },
    baseURL() { return import.meta.env.BASE_URL; },
    currentLocale() {
      // console.log("[header] locale: ", i18n.global.locale)
      return this.$t(`adm.lang.${i18n.global.locale}`);
    },
    languages() { return languageOptions(this.$t); },
    showAppsLink() {
      return window.APP_CONFIG.apps.showLink as boolean ?? false;
    }
  },
});
</script>
